import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectorOverlay } from '../common';
import { destinationService } from '../../api/services';
import { useDeepLTranslated } from '../../hooks/useDeepLTranslated';

const CATEGORY_INCHEON = 'incheon';
const CATEGORY_GIMPO = 'gimpo';

function getCategory(code) {
  if (code.startsWith('GMP')) return CATEGORY_GIMPO;
  return CATEGORY_INCHEON;
}

/**
 * 공항 선택 — SelectorOverlay 기반 (사이드바 + 그룹)
 */
export default function AirportSelector({
  value,
  onChange,
  placeholder,
  className = '',
  variant = 'default',
}) {
  const { t } = useTranslation();
  const effectivePlaceholder = placeholder || t('common.search_airport');
  const categorySidebar = [
    { id: 'all', label: t('common.korea') },
    { id: CATEGORY_INCHEON, label: t('common.incheon_airport') },
    { id: CATEGORY_GIMPO, label: t('common.gimpo_airport') },
  ];
  const categoryLabels = {
    [CATEGORY_INCHEON]: t('common.incheon_airport'),
    [CATEGORY_GIMPO]: t('common.gimpo_airport'),
  };
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [airports, setAirports] = useState([]);

  useEffect(() => {
    destinationService.getAirports().then((data) => {
      setAirports(data.map((a) => {
        // #107: 공항 다국어 — 키가 있으면 그걸 사용, 없으면 API 원래 이름 fallback
        const i18nKey = `common.airport_${a.code}`;
        const translated = t(i18nKey);
        const label = translated !== i18nKey ? translated : a.name;
        return { value: a.code, label, category: getCategory(a.code) };
      }));
    }).catch((e) => console.warn(e.message));
  }, [t]);

  // i18n 매칭 실패로 한국어가 남은 라벨은 DeepL로 보정 (useDeepLTranslated 훅이 안정적으로 처리)
  const labelBundle = useMemo(() => airports.map((a) => a.label), [airports]);
  const tLabels = useDeepLTranslated(labelBundle);
  const tAirports = useMemo(
    () => airports.map((a, i) => ({ ...a, label: tLabels[i] || a.label })),
    [airports, tLabels]
  );

  const displayLabel = useMemo(() => {
    if (!value) return null;
    const found = tAirports.find((a) => a.value === value);
    return found ? found.label : null;
  }, [value, tAirports]);

  const groupedByCategory = useMemo(() => {
    const order = [CATEGORY_INCHEON, CATEGORY_GIMPO];
    return order
      .map((key) => ({ key, label: key, items: tAirports.filter((a) => a.category === key) }))
      .filter((g) => g.items.length > 0);
  }, [tAirports]);

  const filteredData = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (q) {
      const matched = tAirports.filter((a) => a.label.toLowerCase().includes(q));
      return { mode: 'search', results: matched };
    }

    if (activeCategory === 'all') {
      return { mode: 'grouped', groups: groupedByCategory };
    }

    const items = tAirports.filter((a) => a.category === activeCategory);
    return { mode: 'single', label: activeCategory, items };
  }, [search, activeCategory, tAirports, groupedByCategory]);

  const handleOpen = () => {
    if (!open) {
      setSearch('');
      setActiveCategory('all');
    }
    setOpen((v) => !v);
  };

  const handleSelect = (airport) => {
    onChange && onChange(airport.value, airport.label);
    setOpen(false);
    setSearch('');
  };

  const renderItem = (airport) => {
    const isSelected = airport.value === value;
    return (
      <button
        key={airport.value}
        type="button"
        onClick={() => handleSelect(airport)}
        className={`text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
          isSelected
            ? 'text-orange-500 font-semibold bg-orange-50'
            : 'text-mono-700 hover:bg-mono-50 hover:text-mono-900'
        }`}
      >
        {airport.label}
      </button>
    );
  };

  return (
    <SelectorOverlay
      displayLabel={displayLabel}
      placeholder={effectivePlaceholder}
      className={className}
      variant={variant}
      open={open}
      onOpen={handleOpen}
      onClose={() => setOpen(false)}
      maxWidth="620px"
      search={search}
      onSearchChange={(e) => setSearch(e.target.value)}
      searchPlaceholder={t('common.search_airport')}
      title={t('common.main_airports_quick')}
    >
      <div className="flex flex-col sm:flex-row" style={{ minHeight: '200px' }}>
        {/* Mobile: 가로 탭 */}
        {!search.trim() && (
          <div className="sm:hidden flex border-b border-mono-100 overflow-x-auto scrollbar-hide px-3 py-1.5 gap-1.5 shrink-0">
            {categorySidebar.map((cat) => {
              const isActive = cat.id === activeCategory;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setActiveCategory(cat.id)}
                  className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                    isActive ? 'bg-orange-500 text-white' : 'bg-mono-100 text-mono-500'
                  }`}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>
        )}
        {/* PC: 세로 사이드바 */}
        {!search.trim() && (
          <div className="hidden sm:block w-[100px] flex-shrink-0 bg-mono-50 border-r border-mono-100 overflow-y-auto py-2">
            {categorySidebar.map((cat) => {
              const isActive = cat.id === activeCategory;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setActiveCategory(cat.id)}
                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                    isActive
                      ? 'text-orange-500 font-bold border-l-2 border-orange-500 bg-white'
                      : 'text-mono-500 hover:text-mono-700 border-l-2 border-transparent'
                  }`}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-5">
          {filteredData.mode === 'search' && (
            filteredData.results.length === 0 ? (
              <p className="text-sm text-mono-400 text-center py-8">{t('common.no_search_results')}</p>
            ) : (
              <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                {filteredData.results.map(renderItem)}
              </div>
            )
          )}

          {filteredData.mode === 'grouped' && (
            groupedByCategory.map((group) => (
              <div key={group.key} className="mb-6 last:mb-0">
                <div className="text-base font-bold text-mono-900 mb-3 pb-2 border-b border-mono-100">
                  {categoryLabels[group.label] || group.label}
                </div>
                <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                  {group.items.map(renderItem)}
                </div>
              </div>
            ))
          )}

          {filteredData.mode === 'single' && (
            <>
              <div className="text-base font-bold text-mono-900 mb-3 pb-2 border-b border-mono-100">
                {categoryLabels[filteredData.label] || filteredData.label}
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                {filteredData.items.map(renderItem)}
              </div>
            </>
          )}
        </div>
      </div>
    </SelectorOverlay>
  );
}
